import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { Deferred } from 'src/app/common/deferred/deferred';
import { CodePushService } from 'src/app/shared/services/code-push/code-push.service';
import { buildNumber, version } from 'src/environments/build-number';
import { environment } from 'src/environments/environment';

import makeDebug from '../../../../makeDebug';

const debug = makeDebug('services:environment');

export const Branch = {
  dev: 'dev',
  staging: 'staging',
  production: 'production',
  uat: 'uat',
};

export const AlbertaUrls = new InjectionToken<{ dev: string; staging: string; production: string; uat: string }>(
  'alberta baseUrls',
  {
    providedIn: 'root',
    factory: () => environment.environmentSettings.albertaUrl,
  }
);

export const SwodocUrls = new InjectionToken<{ dev: string; staging: string; production: string }>('swodocUrls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.swodocUrls,
});

export const EnvironmentBranch = new InjectionToken<string>('environment branch', {
  providedIn: 'root',
  factory: () => ((environment as any).branch ? (environment as any).branch : null),
});

export const WindowHost = new InjectionToken<string>('window host', {
  providedIn: 'root',
  factory: () => window.location.host.split(':')[0],
});

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  private _ready = new Deferred<void>();
  public get ready(): Promise<void> {
    return this._ready.promise;
  }
  private _version: string;
  public get version() {
    return this._version;
  }

  private _buildNumber: string;
  public get buildNumber() {
    return this._buildNumber;
  }

  private _swodocUrl: string;
  public get swodocUrl() {
    return this._swodocUrl;
  }

  private _branch: string;
  public get branch() {
    return this._branch;
  }

  private _isProduction = false;
  public get isProduction() {
    return this._isProduction;
  }

  private _alberta_baseurl: string;
  public get alberta_baseurl() {
    return this._alberta_baseurl;
  }

  constructor(
    private _platform: Platform,
    @Inject(AlbertaUrls) private _albertaBaseUrls: { dev: string; staging: string; production: string; uat: string },
    @Inject(SwodocUrls) private _swodocUrls: { dev: string; staging: string; production: string },
    @Inject(EnvironmentBranch) private _environmentBranch: string,
    @Inject(WindowHost) private _windowHost: string,
    private _codePushService: CodePushService,
    private _storage: Storage
  ) {}

  public async init() {
    this.initVersion();
    if (this._platform.is('cordova')) {
      await this.initCordova();
    } else {
      await this.initWeb();
    }
    this._ready.resolve();
  }

  private initVersion() {
    this._version = version;
    this._buildNumber = buildNumber;
  }

  private async getDeploymentOverride() {
    if (this._environmentBranch) {
      return this._environmentBranch;
    }
    const branchFromStorage = await this._storage.get('BRANCH');
    if (branchFromStorage) {
      if (
        branchFromStorage === Branch.dev ||
        branchFromStorage === Branch.staging ||
        branchFromStorage === Branch.production
      ) {
        return branchFromStorage;
      }
    }
  }

  private async initCordova() {
    let deployment = await this.getDeploymentOverride();
    if (!deployment) {
      deployment = this._codePushService.currentDeployment
        ? this._codePushService.currentDeployment
        : Branch.production;
      if (deployment) {
        debug('deployment from codepush', deployment);
      }
    }
    await this.initAlbertaUrls(deployment);
  }

  private async initWeb() {
    const deployment = await this.getDeploymentOverride();
    await this.initAlbertaUrls(deployment);
  }

  private async initAlbertaUrls(deployment: string) {
    if (this._platform.is('cordova')) {
      this._branch = deployment ? deployment : Branch.production;
      switch (this._branch) {
        case Branch.dev:
          this._alberta_baseurl = this._albertaBaseUrls.dev;
          this._swodocUrl = this._swodocUrls.dev;
          break;
        case Branch.staging:
          this._alberta_baseurl = this._albertaBaseUrls.staging;
          this._swodocUrl = this._swodocUrls.staging;
          break;
        case Branch.uat:
          this._alberta_baseurl = this._albertaBaseUrls.uat;
          this._swodocUrl = this._swodocUrls.staging;
          break;
        default:
          this._alberta_baseurl = this._albertaBaseUrls.production;
          this._swodocUrl = this._swodocUrls.production;
          this._isProduction = true;
      }
      return;
    }
    switch (this._windowHost) {
      case 'localhost':
      case '127.0.0.1':
      case `dev.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.dev;
        this._swodocUrl = this._swodocUrls.dev;
        this._branch = deployment ? deployment : Branch.dev;
        break;
      case `staging.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.staging;
        this._swodocUrl = this._swodocUrls.staging;
        this._branch = deployment ? deployment : Branch.staging;
        break;
      case `uat.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.uat;
        this._swodocUrl = this._swodocUrls.staging;
        this._branch = deployment ? deployment : Branch.uat;
        break;
      default:
        this._alberta_baseurl = this._albertaBaseUrls.production;
        this._swodocUrl = this._swodocUrls.production;
        this._branch = deployment ? deployment : Branch.production;
        this._isProduction = true;
    }
    debug('_alberta_baseurl', this.alberta_baseurl);
    debug('_swodocUrl', this._swodocUrl);
    debug('_branch', this._branch);
  }
}
